import { List, Map } from 'immutable'
import {
  Action,
  any,
  AsyncState,
  browserRouter,
  Fun,
  loadingAsyncState,
  nothing,
  Option,
  Pair,
  unloadedAsyncState,
  Widget,
  async,
  onlyIf,
  IOWidget
} from 'widgets-for-react'
import { Block } from '../wordpress/types_and_parser/standard_blocks.gen'
import { BlocksRenderer } from '../cms/blocks/block_to_widget.gen'
import { Breadcrumb } from '../wordpress/types_and_parser/extra_types.mod'
import {
  defaultPage,
  HeaderState,
  ContentType,
  emptyAppState,
  PublicSiteState,
  OptionsState
} from '../public_site.main.mod'
import { EntryClient } from '../cms/widget.gen'
import { ExtraImages } from './utils/extra_images.mod'
import { Page } from '../cms/page/types.gen'
import { pageDisplay } from './blocks/renderer.mod'
import { PageStateZero } from '../cms/state/utils.gen'
import { PageTemplateState, AppState } from '../cms/state/types.gen'
import { ParsedNode } from '../wordpress/types_and_parser/types.ext.gen'
import { PublicSiteCustomRoutes, CustomPages } from './custom_pages/types.mod'
import { TransformStandardBlock, WordPressBlock_To_WidgetBlock } from '../wordpress/wordpress_to_cms.gen'
import { WordPress } from '../wordpress/wordpress_api'
import { WordPressBlockImagesLoader } from '../wordpress/rendering/image_utils.mod'
import { CustomBlock } from '../wordpress/types_and_parser/custom_blocks.mod'
import { CustomBlockData } from './blocks/custom_blocks/types.mod'
import { history } from './templates/history.mod'
import { cookiebar } from './templates/cookiebar.mod'
import { ExtraParameters } from '../cms/router/type.gen'
import { lifecycle } from './templates/lifecycle.mod'
import { CustomBlock_To_Widget } from './CustomBlockToWidget'

export type PublicSite_CustomPageData = {
  posts: Map<number, AsyncState<WordPress.PostTypeResponse>>
  post: WordPress.PostTypeResponse
  latest_articles: AsyncState<Array<WordPress.PostTypeResponse>>
  updates: WordPress.Overview
  faqs: AsyncState<Array<WordPress.FaqResponse>>
  forms: AsyncState<Array<WordPress.PostTypeResponse>>
  products: AsyncState<Array<WordPress.ProductResponse>>
  extra_params?: ExtraParameters
  date: string
}

const closeSearchAndMenu = s1 => (
  {
    ...s1,
    state: {
      ...s1.state,
      header: {
        ...s1.state.header,
        menu: 'closed',
        showSearchBar: false,
        hoveredItem: null,
      }
    }
  }
)

export function asyncPost<PT>(id: number): AsyncState<WordPress.PostTypeResponse> {
  return loadingAsyncState<WordPress.PostTypeResponse>(_ =>
    WordPress.api()
      .anypost(id)
      .then(response =>
        response.data ? { kind: 'result', status: 200, value: response.data } : { kind: 'failed', status: 404 }
      )
  )
}

export function asyncPosts(args?: WordPress.PostRequestArguments): AsyncState<Array<WordPress.PostTypeResponse>> {
  return loadingAsyncState<Array<WordPress.PostTypeResponse>>(_ =>
    WordPress.api<WordPress.PostTypeResponse>()
      .select('post')
      .where(args)
      .getMultiple()
      .then(response =>
        response.data
          ? { kind: 'result', status: 200, value: response.data }
          : {
            kind: 'failed',
            status: 404
          }
      )
  )
}

function loadExtraPostData(post: WordPress.PostTypeResponse): PublicSite_CustomPageData {

  let posts = Map<number, AsyncState<WordPress.PostTypeResponse>>()
  let latest_articles = unloadedAsyncState<Array<WordPress.PostTypeResponse>>()
  let updates: WordPress.Overview
  let faqs = unloadedAsyncState<Array<WordPress.FaqResponse>>()
  let forms = unloadedAsyncState<Array<WordPress.PostTypeResponse>>()
  let products = unloadedAsyncState<Array<WordPress.ProductResponse>>()

  if (post.type == 'page' && post.template == 'update-overview') {
    // let currentPage: number = window.history.state && window.history.state.state && window.history.state.state.page ? window.history.state.state.page as number : 1
    updates = {
      current_page: 0,
      async: loadingAsyncState<WordPress.OverviewResponse>(_ =>
        WordPress.api<WordPress.UpdateResponse>()
          .select('update')
          .where({ per_page: 12 })
          .getMultiple()
          .then(response => response.data
            ? {
              kind: 'result', status: 200, value: {
                total_pages: response.headers["x-wp-totalpages"],
                total_articles: response.headers["x-wp-total"],
                kind: 'update',
                posts: response.data
              }
            }
            : {
              kind: 'failed',
              status: 404
            }
          )
      )
    }
  }

  if (post.type == 'page' && post.template == 'form-overview') {
    forms = loadingAsyncState<Array<WordPress.PostTypeResponse>>(_ =>
      WordPress.api<WordPress.BlogResponse>()
        .select('form')
        .where({ per_page: 99 })
        .getMultiple()
        .then(response =>
          response.data ?
            { kind: 'result', status: 200, value: response.data }
            : {
              kind: 'failed',
              status: 404
            }
        )
    )
  }

  if (post.type == 'page' && post.template == 'faq-overview') {
    faqs = loadingAsyncState<Array<WordPress.FaqResponse>>(_ =>
      WordPress.api<WordPress.FaqResponse>()
        .select('faq')
        .where({ orderby: 'menu_order', order: 'asc', per_page: 99 })
        .getMultiple()
        .then(response =>
          response.data
            ? { kind: 'result', status: 200, value: response.data }
            : {
              kind: 'failed',
              status: 404
            }
        )
    )
  }

  if (post.type == 'page' && post.template == 'product-overview') {
    products = loadingAsyncState<Array<WordPress.ProductResponse>>(_ =>
      WordPress.api<WordPress.ProductResponse>()
        .select('product')
        .where({ per_page: 99 })
        .getMultiple()
        .then(response =>
          response.data
            ? { kind: 'result', status: 200, value: response.data }
            : {
              kind: 'failed',
              status: 404
            }
        )
    )
  }

  return {
    posts,
    post,
    latest_articles,
    updates,
    faqs,
    forms,
    products,
    date: post.date
  }
}

export const InitPagesStates: (
  blocks_renderer: BlocksRenderer<CustomBlockData, ExtraImages, PublicSite_CustomPageData>
) => PageTemplateState<CustomBlockData, CustomBlock, Block, ExtraImages, PublicSite_CustomPageData, PublicSiteState> = b => ({
  page: PageStateZero<CustomBlockData, CustomBlock, Block, ExtraImages, PublicSite_CustomPageData, PublicSiteState>(
    () => unloadedAsyncState(),
    defaultPage,
    pageDisplay(b)
  ).page
})

export const get_page = (
  slug: string,
  extra_parameters: ExtraParameters
): AsyncState<Page<CustomBlock, Block, PublicSite_CustomPageData>> =>
  loadingAsyncState(_ => {
    return WordPress.api()
      .current(slug)
      .then(response => {
        return response.data && response.data.acf
          ? {
            kind: 'result',
            value: {
              blocks: response.data.custom_blocks ? response.data.custom_blocks.map(b => TransformStandardBlock(b)) : [],
              extra_data: { ...loadExtraPostData(response.data), extra_params: extra_parameters }
            },
            status: 200
          }
          : { kind: 'failed', status: 404 }
      })
      .catch(e => {
        console.error(e)
        return e
      })
  })

export function get_options(id: string): AsyncState<WordPress.OptionsResponse> {
  return loadingAsyncState<WordPress.OptionsResponse>(_ =>
    WordPress.api()
      .options(id)
      .then(response => {
        return response.data
          ? { kind: 'result', status: 200, value: response.data }
          : {
            kind: 'failed',
            status: 404
          }
      })
  )
}

const getPost = (id: number) =>
  WordPress.api()
    .anypost(id)
    .then(response => {
      // console.log('response', response)
      return response.data
        ? ({ kind: 'result', status: 200, value: response.data } as any)
        : {
          kind: 'failed',
          status: 404
        }
    })
    .catch(e => {
      // console.log(e)
      return { kind: 'failed', status: 500 } as any
    })

export const application = (
  data: { rootpath: string; rootpath_to_ignore: string; login_address?: string },
  header: (_: HeaderState, o: OptionsState) => Widget<Action<HeaderState>>,
  footer: (
    _: AppState<PublicSiteState, PublicSiteCustomRoutes>
  ) => Widget<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>,
  errorPage: IOWidget<PublicSiteState, never>,
  loadingPage: Widget<never>,
  page: Fun<Pair<List<Breadcrumb>, Option<ParsedNode>>, ContentType>,
  custom_pages: CustomPages
) => {
  let rootpath = data.rootpath ? data.rootpath : ''
  let rootpath_to_ignore = data.rootpath_to_ignore ? data.rootpath_to_ignore : ''
  return (s0: AppState<PublicSiteState, PublicSiteCustomRoutes>) =>
    browserRouter<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>()(
      s0.route.kind == 'ignore'
        ? nothing()
        : any<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>()([
          async<WordPress.WordlistResponse>(_ => nothing())(s0.state.wordlist).map<
            Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
          >(a => s => {
            let s1 = a(s.state.wordlist)

            return s1.kind == 'loaded'
              ? {
                ...s0,
                state: {
                  ...s0.state,
                  wordlist: {
                    ...s1,
                    value: {
                      ...s1.value,
                      acf: Array.isArray(s1.value.acf) ? {
                        wordlist: []
                      } : s1.value.acf
                    }
                  }
                }
              }
              : s0
          }),

          async<WordPress.OptionsResponse>(_ => nothing())(s0.state.options).map<
            Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
          >(a => s => {
            let s1 = a(s.state.options)

            return s1.kind == 'loaded'
              ? {
                ...s0,
                state: {
                  ...s0.state,
                  options: s1
                }
              }
              : s0
          }),

          s0.state.page.page.kind == 'loaded'
            ? async<Array<WordPress.PostTypeResponse>>(_ => nothing())(
              s0.state.page.page.value.extra_data.latest_articles
            ).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          latest_articles: a(s.state.page.page.value.extra_data.latest_articles)
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),

          s0.state.page.page.kind == 'loaded' &&
            s0.state.page.page.value.extra_data.post.type == 'page' &&
            s0.state.page.page.value.extra_data.post.template == 'update-overview'
            ? async<WordPress.OverviewResponse>(_ => nothing())(s0.state.page.page.value.extra_data.updates.async).map<
              Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
            >(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          updates: {
                            ...s.state.page.page.value.extra_data.updates,
                            async: a(s.state.page.page.value.extra_data.updates.async)
                          }
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),

          s0.state.page.page.kind == 'loaded' &&
            s0.state.page.page.value.extra_data.post.type == 'page' &&
            s0.state.page.page.value.extra_data.post.template == 'form-overview'
            ? async<Array<WordPress.PostTypeResponse>>(_ => nothing())(s0.state.page.page.value.extra_data.forms).map<
              Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
            >(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          forms: a(s.state.page.page.value.extra_data.forms)
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),

          s0.state.page.page.kind == 'loaded' &&
            s0.state.page.page.value.extra_data.post.type == 'page' &&
            s0.state.page.page.value.extra_data.post.template == 'faq-overview'
            ? async<Array<WordPress.FaqResponse>>(_ => nothing())(s0.state.page.page.value.extra_data.faqs).map<
              Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
            >(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          faqs: a(s.state.page.page.value.extra_data.faqs)
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),

          s0.state.page.page.kind == 'loaded' &&
            s0.state.page.page.value.extra_data.post.type == 'page' &&
            s0.state.page.page.value.extra_data.post.template == 'product-overview'
            ? async<Array<WordPress.ProductResponse>>(_ => nothing())(s0.state.page.page.value.extra_data.products).map<
              Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
            >(a => s => {
              if (s.state.page.page.kind != 'loaded') return s

              return {
                ...s,
                state: {
                  ...s.state,
                  page: {
                    ...s.state.page,
                    page: {
                      ...s.state.page.page,
                      value: {
                        ...s.state.page.page.value,
                        extra_data: {
                          ...s.state.page.page.value.extra_data,
                          products: a(s.state.page.page.value.extra_data.products)
                        }
                      }
                    }
                  }
                }
              }
            })
            : nothing(),

          async<Array<WordPress.UpdateResponse>>(_ => nothing())(s0.state.latest_updates).map<
            Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
          >(a => s => {
            return {
              ...s,
              state: {
                ...s.state,
                latest_updates: a(s.state.latest_updates)
              }
            }
          }),

          any<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>()(
            s0.state.page.page.kind == 'loaded'
              ? s0.state.page.page.value.extra_data.posts
                .map((p, key) =>
                  async<WordPress.PostTypeResponse>(_ => nothing())(p).map<
                    Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>
                  >(a => s => {
                    if (s.state.page.page.kind != 'loaded') return s
                    let a1 = a(s.state.page.page.value.extra_data.posts.get(key))
                    return {
                      ...s,
                      state: {
                        ...s.state,
                        page: {
                          ...s.state.page,
                          page: {
                            ...s.state.page.page,
                            value: {
                              ...s.state.page.page.value,
                              extra_data: {
                                ...s.state.page.page.value.extra_data,
                                posts: s.state.page.page.value.extra_data.posts.set(key, a1)
                              }
                            }
                          }
                        }
                      }
                    }
                  })
                )
                .valueSeq()
                .toArray()
              : [nothing()]
          ),
          onlyIf(s0.route.kind != 'styleguide',
            header(s0.state.header, s0.state.options).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(
              a => s1 => ({
                ...s1,
                state: { ...s1.state, header: a(s1.state.header) }
              })
            )
          ),
          EntryClient<
            CustomBlockData,
            CustomBlock,
            Block,
            PublicSiteState,
            keyof CustomPages,
            PublicSiteCustomRoutes,
            ExtraImages,
            PublicSite_CustomPageData
          >({
            initState: emptyAppState(page),
            custom_pages,
            StandardBlockImagesLoader: WordPressBlockImagesLoader,
            StandardBlock_To_Widget: WordPressBlock_To_WidgetBlock,
            errorPage: errorPage,
            loadingPage: loadingPage,
            rootpath: rootpath,
            rootpath_to_ignore: rootpath_to_ignore,
            CustomBlock_To_Widget: CustomBlock_To_Widget,
            CustomBLockImagesLoader: (c: CustomBlock) => {
              // if (c.kind == 'exampleBlock') {
              //   //let img = mk_pair(c.image, loadMedia(c.image) as AsyncState<Map<ImageSize<ExtraImages>, Url>>)
              //   //improve when personBlock is ready in drupal/parsed_types
              //   return []
              // }
              return []
            }
          })(s0),
          // s0.state.latest_updates.kind == 'loaded' ? 
          //   LatestBlock(
          //     {
          //       title: 'Laatste updates',
          //       button: {
          //         title: 'Bekijk alle updates',
          //         url: '/updates'
          //       },
          //       hasBackground: false,
          //       display: { kind: 'latest' }
          //     },
          //     { style: {} }
          //   )({} as CMSState<{}, {}, never>)

          //   latestUpdates(s0.state.latest_updates).never() 
          // : nothing(),
          onlyIf(s0.route.kind != 'styleguide',
            footer(s0).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(a => s1 => a(s1))
          ),
          onlyIf(s0.route.kind != 'styleguide',
            cookiebar(s0.state.cookiebar).map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(a => s1 => ({
              ...s1,
              state: { ...s1.state, cookiebar: a(s1.state.cookiebar) }
            }))
          ),
          history().map<Action<AppState<PublicSiteState, PublicSiteCustomRoutes>>>(la => {
            la.action == 'PUSH' && window.scrollTo(0, 0)
            return closeSearchAndMenu
          }),
          lifecycle<AppState<PublicSiteState, PublicSiteCustomRoutes>>(
            a => {
              document.addEventListener('click', captureDocumentClick(a)),
                document.addEventListener('click', captureDefinitionWordClick(a))
            },
            a => {
              document.removeEventListener('click', captureDocumentClick(a)),
                document.removeEventListener('click', captureDefinitionWordClick(a))
            }
          )(_ => nothing())(s0),
        ])
    ).map<AppState<PublicSiteState, PublicSiteCustomRoutes>>(a => {
      let s1 = a(s0)

      if (
        s1.state.page.page.kind == 'loaded' &&
        s1.state.page.page.value.extra_data.post.yoast &&
        s1.state.page.page.value.extra_data.post.yoast.title
      ) {
        document.title = s1.state.page.page.value.extra_data.post.yoast.title
      }

      if (s1.state.header.menu == 'open' || s1.state.header.showSearchBar) {
        document.body.setAttribute('data-scroll', 'locked')
      } else {
        document.body.setAttribute('data-scroll', '')
      }

      return s1
    })
}

type MouseClickEvent = MouseEvent & { target: HTMLAnchorElement }

const captureDocumentClick = a => (e: MouseClickEvent) => {
  const el: HTMLAnchorElement = e.target
  let trigger: boolean = false
  if (el.tagName && el.tagName.toLowerCase() == 'a' && el.href.includes('#cookiesettings')) {
    e.preventDefault()
    e.stopPropagation()
    trigger = true
  }
  a(s1 =>
  (trigger ? {
    ...s1,
    state: {
      ...s1.state,
      cookiebar: {
        ...s1.state.cookiebar,
        cookiebar: (s1.state.cookiebar.cookiebar == 'open' ? 'closed' : 'open')
      }
    }
  } : s1)
  )
}

const captureDefinitionWordClick = a => (e: MouseClickEvent) => {
  const el: HTMLAnchorElement = e.target;

  if (el.tagName && el.tagName.toLowerCase() == 'button' && el.classList.contains('dfn')) {
    // Handle button attributes
    el.classList.toggle('dfn--show');
    const currentState = el.classList.contains('dfn--show');
    el.setAttribute('aria-expanded', `${currentState}`);

    // Handle definition attributes
    const elDfn: HTMLElement = document.documentElement.querySelector(`#${el.getAttribute('aria-controls')}`);

    if (elDfn == null || typeof elDfn == 'undefined') { return; }

    elDfn.setAttribute('aria-hidden', `${!currentState}`);
  }
}



// WEBPACK FOOTER //
// ./Client/components/public_site/application.mod.ts